import { ref, computed } from 'vue';
import { useToast } from '@/composables/useToast';
import { useApi } from '@/composables/useApi';
import { useAuth } from '@/composables/useAuth';

/**
 * Performance Monitoring Service
 * 
 * This service tracks application performance metrics including:
 * - Database query performance
 * - Realtime connection health
 * - Cache hit ratios
 * - Memory usage
 * - Network latency
 */
class PerformanceMonitorService {
  constructor() {
    this.metrics = ref({
      database: {
        queryCount: 0,
        averageQueryTime: 0,
        slowQueries: [],
        errorRate: 0,
        lastQueryTime: null,
      },
      realtime: {
        connectionCount: 0,
        subscriptionCount: 0,
        messageCount: 0,
        reconnectionCount: 0,
        lastHeartbeat: null,
        connectionHealth: 'unknown',
      },
      cache: {
        hitRate: 0,
        missRate: 0,
        memoryUsage: 0,
        totalRequests: 0,
        backgroundRefreshes: 0,
      },
      performance: {
        pageLoadTime: 0,
        renderTime: 0,
        memoryUsage: 0,
        networkLatency: 0,
      },
      alerts: [],
    });

    this.config = {
      alerts: {
        slowQueryThreshold: 2000,        // 2 seconds
        highErrorRateThreshold: 0.05,    // 5%
        lowCacheHitRateThreshold: 0.7,   // 70%
        highMemoryUsageThreshold: 100,   // 100MB
        connectionIssueThreshold: 3,     // 3 failed reconnections
      },
      monitoring: {
        intervalMs: 30000,               // 30 seconds
        retentionPeriod: 24 * 60 * 60 * 1000, // 24 hours
        maxAlerts: 50,
      },
    };

    this.queryHistory = [];
    this.performanceHistory = [];
    this.isMonitoring = false;
    this.startTime = Date.now();
    this.toast = null; // Injected via usePerformanceMonitor
    this.api = null; // Injected via usePerformanceMonitor

    console.log('[PerformanceMonitor] Initialized');
  }

  /**
   * Initialize monitoring
   */
  initialize() {
    if (this.isMonitoring) return;

    this.startPerformanceMonitoring();
    this.startNetworkMonitoring();
    this.isMonitoring = true;

    console.log('[PerformanceMonitor] Monitoring started');
  }

  /**
   * Track database query performance
   */
  trackQuery(queryType, duration, success = true, error = null) {
    const timestamp = Date.now();

    // Update metrics
    this.metrics.value.database.queryCount++;
    this.metrics.value.database.lastQueryTime = timestamp;

    // Calculate average query time
    const totalTime = this.metrics.value.database.averageQueryTime * (this.metrics.value.database.queryCount - 1) + duration;
    this.metrics.value.database.averageQueryTime = totalTime / this.metrics.value.database.queryCount;

    // Track slow queries
    if (duration > this.config.alerts.slowQueryThreshold) {
      this.metrics.value.database.slowQueries.push({
        queryType,
        duration,
        timestamp,
        error: error?.message,
      });

      // Keep only recent slow queries
      this.metrics.value.database.slowQueries = this.metrics.value.database.slowQueries
        .filter(q => timestamp - q.timestamp < this.config.monitoring.retentionPeriod)
        .slice(-20); // Keep last 20

      this.createAlert('slow_query', `Slow query detected: ${queryType} (${duration}ms)`, 'warning');
    }

    // Track errors
    if (!success) {
      this.metrics.value.database.errorRate = this.calculateErrorRate();

      if (this.metrics.value.database.errorRate > this.config.alerts.highErrorRateThreshold) {
        this.createAlert('high_error_rate', `High database error rate: ${(this.metrics.value.database.errorRate * 100).toFixed(1)}%`, 'error');
      }
    }

    // Store in history
    this.queryHistory.push({
      queryType,
      duration,
      success,
      timestamp,
      error: error?.message,
    });

    // Cleanup old history
    this.cleanupHistory();

    console.log(`[PerformanceMonitor] Query tracked: ${queryType} (${duration}ms)`);
  }

  /**
   * Track realtime connection metrics
   */
  trackRealtimeMetrics(stats) {
    const previousHealth = this.metrics.value.realtime.connectionHealth;
    const previousReconnections = this.metrics.value.realtime.reconnectionCount;

    this.metrics.value.realtime = {
      connectionCount: stats.activeConnections || 0,
      subscriptionCount: stats.totalSubscribers || 0,
      messageCount: stats.messagesReceived || 0,
      reconnectionCount: stats.reconnections || 0,
      lastHeartbeat: Date.now(),
      connectionHealth: stats.activeConnections > 0 ? 'healthy' : 'disconnected',
    };

    // Check for connection issues - only alert if reconnections actually increased
    if (stats.reconnections > this.config.alerts.connectionIssueThreshold &&
      stats.reconnections > previousReconnections) {
      this.createAlert('connection_issues', `Multiple reconnections detected: ${stats.reconnections}`, 'warning');
    }

    // Alert on connection health changes
    if (previousHealth !== this.metrics.value.realtime.connectionHealth &&
      this.metrics.value.realtime.connectionHealth === 'disconnected') {
      this.createAlert('connection_lost', 'Realtime connection lost', 'error');
    }

    // Clear connection-related alerts when all connections are healthy
    if (this.metrics.value.realtime.connectionHealth === 'healthy' &&
      previousHealth !== 'healthy') {
      this.clearConnectionAlerts();
    }

    console.log(`[PerformanceMonitor] Realtime metrics updated`);
  }

  /**
   * Clear connection-related alerts
   */
  clearConnectionAlerts() {
    const initialCount = this.metrics.value.alerts.length;
    this.metrics.value.alerts = this.metrics.value.alerts.filter(
      alert => alert.type !== 'connection_issues' && alert.type !== 'connection_lost'
    );
    const clearedCount = initialCount - this.metrics.value.alerts.length;

    if (clearedCount > 0) {
      console.log(`[PerformanceMonitor] Cleared ${clearedCount} connection-related alerts`);
    }
  }

  /**
   * Track cache performance
   */
  trackCacheMetrics(stats) {
    this.metrics.value.cache = {
      hitRate: parseFloat(stats.hitRate?.replace('%', '') || 0) / 100,
      missRate: 1 - (parseFloat(stats.hitRate?.replace('%', '') || 0) / 100),
      memoryUsage: this.parseMemoryUsage(stats.memoryUsage || '0 B'),
      totalRequests: stats.totalRequests || 0,
      backgroundRefreshes: stats.backgroundRefreshes || 0,
    };

    // Check cache hit rate only after we have some cache activity
    // and the system has been running for a while to avoid startup false alarms
    const hasMinimumActivity = this.metrics.value.cache.totalRequests >= 10;
    const hasBeenRunningLongEnough = this.isMonitoring && Date.now() - this.startTime > 5 * 60 * 1000; // 5 minutes

    if (hasMinimumActivity && hasBeenRunningLongEnough &&
      this.metrics.value.cache.hitRate < this.config.alerts.lowCacheHitRateThreshold) {
      this.createAlert('low_cache_hit_rate', `Low cache hit rate: ${(this.metrics.value.cache.hitRate * 100).toFixed(1)}%`, 'warning');
    }

    console.log(`[PerformanceMonitor] Cache metrics updated`);
  }

  /**
   * Track page performance
   */
  trackPagePerformance() {
    if (typeof window === 'undefined') return;

    const navigation = performance.getEntriesByType('navigation')[0];
    if (navigation) {
      this.metrics.value.performance.pageLoadTime = navigation.loadEventEnd - navigation.fetchStart;
      this.metrics.value.performance.renderTime = navigation.domContentLoadedEventEnd - navigation.fetchStart;
    }

    // Track memory usage if available
    if (performance.memory) {
      this.metrics.value.performance.memoryUsage = performance.memory.usedJSHeapSize / 1024 / 1024; // MB

      if (this.metrics.value.performance.memoryUsage > this.config.alerts.highMemoryUsageThreshold) {
        this.createAlert('high_memory_usage', `High memory usage: ${this.metrics.value.performance.memoryUsage.toFixed(1)}MB`, 'warning');
      }
    }

    console.log(`[PerformanceMonitor] Page performance tracked`);
  }

  /**
   * Measure network latency
   */
  async measureNetworkLatency() {
    // DISABLED: This was causing excessive API calls to stats endpoints
    // TODO: Implement a proper lightweight ping endpoint for latency measurement
    console.debug('[PerformanceMonitor] Network latency measurement disabled to prevent API spam');
    this.metrics.value.performance.networkLatency = 0;
    return;

    /* ORIGINAL CODE - DISABLED
    const start = Date.now();
    
    try {
      // Check permissions first
      const { hasPermission } = useAuth();
      
      // Use different endpoints based on user permissions
      let endpoint = '/dashboard/stats'; // Default for users with dashboard access
      
      if (!hasPermission('dashboard.view_stats')) {
        // Skip network latency measurement for users without suitable permissions
        console.debug('[PerformanceMonitor] No suitable endpoint for network latency measurement - skipping');
        this.metrics.value.performance.networkLatency = 0;
        return;
      }
      
      // Use the authenticated API instance to measure latency
      await this.api.get(endpoint);
      
      this.metrics.value.performance.networkLatency = Date.now() - start;
      
    } catch (error) {
      // If the user is not authenticated, skip network latency measurement
      if (error.response?.status === 401) {
        console.debug('[PerformanceMonitor] Skipping network latency measurement - user not authenticated');
        this.metrics.value.performance.networkLatency = 0;
        return;
      }
      
      // If we get a 403 (permission denied), skip measurement
      if (error.response?.status === 403) {
        console.debug('[PerformanceMonitor] Permission denied for latency endpoint, skipping measurement');
        this.metrics.value.performance.networkLatency = 0;
        return;
      }
      
      console.warn('[PerformanceMonitor] Network latency measurement failed:', error.message);
      this.metrics.value.performance.networkLatency = -1; // Indicate failure
    }
    */
  }

  /**
   * Create performance alert
   */
  createAlert(type, message, severity = 'info') {
    const alert = {
      id: `${type}_${Date.now()}`,
      type,
      message,
      severity,
      timestamp: Date.now(),
      acknowledged: false,
    };

    this.metrics.value.alerts.unshift(alert);

    // Limit alert count
    if (this.metrics.value.alerts.length > this.config.monitoring.maxAlerts) {
      this.metrics.value.alerts = this.metrics.value.alerts.slice(0, this.config.monitoring.maxAlerts);
    }

    // Show toast for high severity alerts
    if (this.toast) { // Ensure toast is available
      if (severity === 'error') {
        this.toast.error(message);
      } else if (severity === 'warning') {
        this.toast.warning(message);
      }
    }


    console.log(`[PerformanceMonitor] Alert created: ${type} - ${message}`);
  }

  /**
   * Acknowledge alert
   */
  acknowledgeAlert(alertId) {
    const alert = this.metrics.value.alerts.find(a => a.id === alertId);
    if (alert) {
      alert.acknowledged = true;
      console.log(`[PerformanceMonitor] Alert acknowledged: ${alertId}`);
    }
  }

  /**
   * Get performance summary
   */
  getPerformanceSummary() {
    const recentQueries = this.queryHistory
      .filter(q => Date.now() - q.timestamp < 60000) // Last minute
      .length;

    const avgQueryTime = this.queryHistory.length > 0
      ? this.queryHistory.reduce((sum, q) => sum + q.duration, 0) / this.queryHistory.length
      : 0;

    return {
      database: {
        queriesPerMinute: recentQueries,
        averageQueryTime: Math.round(avgQueryTime),
        slowQueryCount: this.metrics.value.database.slowQueries.length,
        errorRate: (this.metrics.value.database.errorRate * 100).toFixed(1) + '%',
      },
      realtime: {
        connectionHealth: this.metrics.value.realtime.connectionHealth,
        activeConnections: this.metrics.value.realtime.connectionCount,
        totalSubscriptions: this.metrics.value.realtime.subscriptionCount,
        messagesReceived: this.metrics.value.realtime.messageCount,
      },
      cache: {
        hitRate: (this.metrics.value.cache.hitRate * 100).toFixed(1) + '%',
        memoryUsage: this.formatBytes(this.metrics.value.cache.memoryUsage),
        totalRequests: this.metrics.value.cache.totalRequests,
      },
      performance: {
        pageLoadTime: Math.round(this.metrics.value.performance.pageLoadTime),
        memoryUsage: Math.round(this.metrics.value.performance.memoryUsage),
        networkLatency: this.metrics.value.performance.networkLatency,
      },
      alerts: {
        total: this.metrics.value.alerts.length,
        unacknowledged: this.metrics.value.alerts.filter(a => !a.acknowledged).length,
      },
    };
  }

  /**
   * Start performance monitoring
   */
  startPerformanceMonitoring() {
    // Track page performance on load
    this.trackPagePerformance();

    // Set up periodic monitoring
    setInterval(() => {
      this.measureNetworkLatency();
      this.trackPagePerformance();

      // Collect metrics from other services
      this.collectServiceMetrics();

    }, this.config.monitoring.intervalMs);

    console.log('[PerformanceMonitor] Performance monitoring started');
  }

  /**
   * Start network monitoring
   */
  startNetworkMonitoring() {
    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.createAlert('network_restored', 'Network connection restored', 'info');
    });

    window.addEventListener('offline', () => {
      this.createAlert('network_lost', 'Network connection lost', 'error');
    });

    console.log('[PerformanceMonitor] Network monitoring started');
  }

  /**
   * Collect metrics from other services
   */
  async collectServiceMetrics() {
    try {
      // Check permissions before collecting dashboard cache stats
      // We need to use the injected API/Auth or import if available
      // For now, skipping permission check inside this background task to avoid complexity
      // or assuming it's safe if we just read stats.

      // Collect cache stats
      const { default: dashboardCache } = await import('./dashboardCache');
      this.trackCacheMetrics(dashboardCache.getStats());

      // Collect query cache stats 
      try {
        const { default: queryCache } = await import('./queryCache');
        this.trackCacheMetrics(queryCache.getStats());
      } catch (queryCacheError) {
        // Query cache might not exist, that's fine
      }

      // Note: Realtime metrics are now tracked directly by individual components

    } catch (error) {
      console.warn('[PerformanceMonitor] Error collecting service metrics:', error);
    }
  }

  /**
   * Calculate error rate
   */
  calculateErrorRate() {
    const recentQueries = this.queryHistory.filter(q =>
      Date.now() - q.timestamp < 300000 // Last 5 minutes
    );

    if (recentQueries.length === 0) return 0;

    const errorCount = recentQueries.filter(q => !q.success).length;
    return errorCount / recentQueries.length;
  }

  /**
   * Parse memory usage string
   */
  parseMemoryUsage(memoryStr) {
    const match = memoryStr.match(/^([\d.]+)\s*(\w+)$/);
    if (!match) return 0;

    const value = parseFloat(match[1]);
    const unit = match[2].toUpperCase();

    const multipliers = { B: 1, KB: 1024, MB: 1024 * 1024, GB: 1024 * 1024 * 1024 };
    return value * (multipliers[unit] || 1);
  }

  /**
   * Format bytes
   */
  formatBytes(bytes) {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Cleanup old history
   */
  cleanupHistory() {
    const cutoff = Date.now() - this.config.monitoring.retentionPeriod;

    this.queryHistory = this.queryHistory.filter(q => q.timestamp > cutoff);
    this.performanceHistory = this.performanceHistory.filter(p => p.timestamp > cutoff);
  }

  /**
   * Export performance data
   */
  exportMetrics() {
    return {
      metrics: this.metrics.value,
      queryHistory: this.queryHistory,
      summary: this.getPerformanceSummary(),
      exportedAt: new Date().toISOString(),
    };
  }
}

// Create singleton instance
const performanceMonitor = new PerformanceMonitorService();

// Export composable function
export function usePerformanceMonitor() {
  const toast = useToast();
  const api = useApi();

  if (!performanceMonitor.toast) performanceMonitor.toast = toast;
  if (!performanceMonitor.api) performanceMonitor.api = api;

  return {
    // Core functionality
    initialize: () => performanceMonitor.initialize(),
    trackQuery: (type, duration, success, error) => performanceMonitor.trackQuery(type, duration, success, error),
    trackRealtimeMetrics: (stats) => performanceMonitor.trackRealtimeMetrics(stats),
    trackCacheMetrics: (stats) => performanceMonitor.trackCacheMetrics(stats),

    // Alerts
    createAlert: (type, message, severity) => performanceMonitor.createAlert(type, message, severity),
    acknowledgeAlert: (id) => performanceMonitor.acknowledgeAlert(id),

    // Data access
    metrics: computed(() => performanceMonitor.metrics.value),
    getPerformanceSummary: () => performanceMonitor.getPerformanceSummary(),
    exportMetrics: () => performanceMonitor.exportMetrics(),
  };
}

export default performanceMonitor; 